// Add-or-edit dialog for custom dashboard cards. Used by DashboardPage when a
// PowerUser+ presses "Add card" or the pencil on a custom card, and by the
// Query page's "Add to dashboard" under a result.
//
// A new card is built one of three ways: from menus (pick a class, then count
// it or break it down by a property, and the dialog writes the query), from a
// saved query, or by writing the query, which is the advanced way. The server
// checks the query on save and its message is shown at the Save button.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { isQros, useMe } from '@/lib/useMe';
import {
  createCard,
  patchCard,
  restoreCard,
  type CardDef,
  type CardKind,
} from '@/lib/useDashboardCards';
import { useSnippets } from '@/lib/useSnippets';
import { labelOf, propertiesOfClass, useOntologySchema } from '@/lib/useOntology';
import { breakdownQuery, countQuery } from '@/lib/queryText';
import { ALL_GRAPHS } from '@/components/GraphSelector';
import { Message } from '@/components/ui/Message';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { fieldClass } from '@/components/ui/field';

// Placeholder queries per card kind, in the active query language. QROS speaks
// QQL (FIND); RDF speaks SPARQL (SELECT). Examples are starting points — the
// server probes the query on save and reports the exact shape it needs.
const QUERY_PLACEHOLDERS: Record<'qros' | 'rdf', Record<CardKind, string>> = {
  qros: {
    count: 'FIND COUNT(?x) AS ?n WHERE { ?x :type :Product }',
    distribution:
      'FIND ?class COUNT(?i) AS ?n WHERE { ?i :type ?class } GROUP BY ?class ORDER BY DESC(?n) LIMIT 10',
    table: 'FIND ?item ?label ?value WHERE { ?item :label ?label ?item :someValue ?value } ORDER BY ?label',
  },
  rdf: {
    count:
      'SELECT (COUNT(?x) AS ?n) WHERE { GRAPH <urn:tres:data> { ?x a <http://example.org/Product> } }',
    distribution:
      'SELECT ?class ?label (COUNT(?i) AS ?n) WHERE { GRAPH <urn:tres:data> { ?i a ?class } OPTIONAL { ?class <http://www.w3.org/2000/01/rdf-schema#label> ?label } } GROUP BY ?class ?label ORDER BY DESC(?n) LIMIT 10',
    table: 'SELECT ?item ?label ?value WHERE { GRAPH <urn:tres:data> { ... } } ORDER BY ?label',
  },
};

const KIND_SHOWS: Record<CardKind, string> = {
  count: 'a single number',
  distribution: 'the top ten as a bar list',
  table: 'a table of values and how many have each',
};

/** A card the dialog opens with, from a query the user already ran. */
export interface CardDraft {
  title: string;
  kind: CardKind;
  sparql: string;
  /** Where the query came from, said under the dialog's title. */
  source: string;
}

type BuildWay = 'menus' | 'saved' | 'write';

interface DashboardCardModalProps {
  open: boolean;
  /** Existing card to edit. `undefined` means "create new". */
  editing?: CardDef | undefined;
  /** A new card's starting point (Query page, Add to dashboard). */
  draft?: CardDraft | undefined;
  /** Soft-deleted cards offered for one-click restore (create mode). */
  deletedCards?: CardDef[];
  onClose: () => void;
  /** Called after a successful save so the parent can refetch. */
  onSaved: (card: { title: string; dashboard: string }) => void;
}

type SaveState =
  | { status: 'idle' }
  | { status: 'saving' }
  | { status: 'error'; message: string };

export function DashboardCardModal({
  open,
  editing,
  draft,
  deletedCards = [],
  onClose,
  onSaved,
}: DashboardCardModalProps) {
  const auth = useAuth();
  const me = useMe();
  const qros = isQros(me.data);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const titleRef = useRef<HTMLInputElement | null>(null);

  const [title, setTitle] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [subtitle, setSubtitle] = useState('');
  const [kind, setKind] = useState<CardKind>('count');
  const [sparql, setSparql] = useState('');
  const [ordinal, setOrdinal] = useState<number>(0);
  const [dashboard, setDashboard] = useState('Reports');
  const [way, setWay] = useState<BuildWay>('menus');
  const [saveState, setSaveState] = useState<SaveState>({ status: 'idle' });

  const handleRestore = async (id: string) => {
    const token = auth.user?.id_token;
    if (!token) {
      setSaveState({ status: 'error', message: 'Not signed in — the card was not restored.' });
      return;
    }
    setRestoringId(id);
    const r = await restoreCard(id, token);
    setRestoringId(null);
    if (!r.ok) {
      setSaveState({
        status: 'error',
        message: r.message,
      });
      return;
    }
    const restored = deletedCards.find((c) => c.id === id);
    onSaved({ title: restored?.title ?? 'The card', dashboard: restored?.dashboard ?? 'Reports' });
    onClose();
  };

  // Reset the form when the dialog opens: an edited card's values, a draft's,
  // or a blank card built from menus.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setTitle(editing.title);
      setSubtitle(editing.subtitle);
      setKind(editing.kind);
      setSparql(editing.sparql);
      setOrdinal(editing.ordinal);
      setDashboard(editing.dashboard);
      setWay('write');
    } else {
      setTitle(draft?.title ?? '');
      setSubtitle('');
      setKind(draft?.kind ?? 'count');
      setSparql(draft?.sparql ?? '');
      setOrdinal(0);
      setDashboard('Reports');
      setWay(draft ? 'write' : 'menus');
    }
    setTitleTouched(!!editing || !!draft);
    setSaveState({ status: 'idle' });
    const id = window.requestAnimationFrame(() => titleRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open, editing, draft]);

  // Escape closes (unless saving).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && saveState.status !== 'saving') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saveState.status, onClose]);

  if (!open) return null;

  const saving = saveState.status === 'saving';
  const titleOk = title.trim().length > 0;
  const sparqlOk = sparql.trim().length > 0;
  const canSubmit = titleOk && sparqlOk && !saving && !!auth.user?.id_token;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const token = auth.user?.id_token;
    if (!token || !titleOk || !sparqlOk) return;
    setSaveState({ status: 'saving' });
    const board = dashboard.trim() || 'Reports';

    const result = editing
      ? await patchCard(
          editing.id,
          {
            title: title.trim(),
            subtitle: subtitle.trim(),
            sparql: sparql.trim(),
            ordinal,
            dashboard: board,
          },
          token,
        )
      : await createCard(
          {
            title: title.trim(),
            subtitle: subtitle.trim() || undefined,
            kind,
            sparql: sparql.trim(),
            ordinal,
            dashboard: board,
          },
          token,
        );

    if (result.ok) {
      onSaved({ title: title.trim(), dashboard: board });
      onClose();
    } else {
      setSaveState({ status: 'error', message: `The card was not saved. ${result.message}` });
    }
  }

  const subtitleText = editing
    ? 'Update the card. Its query runs on the server, across every graph of this database.'
    : draft
      ? `${draft.source} The card counts across every graph of this database, whatever graphs the query page had picked.`
      : 'Adds a card to the Dashboard for everyone on this database. It counts across every graph.';

  return (
    <Modal
      title={editing ? 'Edit dashboard card' : 'Add dashboard card'}
      subtitle={subtitleText}
      titleId="dashboard-card-title"
      onClose={onClose}
      busy={saving}
      size="xl"
      footer={
        <>
          <Button variant="secondary" size="lg" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="dashboard-card-form" variant="primary" size="lg" disabled={!canSubmit}>
            {saving ? (editing ? 'Saving…' : 'Checking the query and saving…') : editing ? 'Save' : 'Create card'}
          </Button>
        </>
      }
    >
      <form id="dashboard-card-form" onSubmit={handleSubmit}>
        {!editing && !draft && deletedCards.length > 0 && (
          <div className="mb-4 rounded-md border border-border bg-muted/30 p-3">
            <div className="text-sm font-medium text-tres-text-primary">Restore a removed card</div>
            <ul className="mt-2 space-y-1">
              {deletedCards.map((c) => (
                <li key={c.id} className="flex items-center gap-2 text-sm">
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => void handleRestore(c.id)}
                    disabled={restoringId !== null}
                  >
                    {restoringId === c.id ? 'Restoring…' : 'Restore'}
                  </Button>
                  <span className="truncate" title={c.subtitle}>
                    {c.title}
                    <span className="ml-1 text-xs text-tres-text-secondary">
                      ({c.kind}, {c.dashboard})
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-2 text-xs text-tres-text-secondary">Or create a new card below.</div>
          </div>
        )}

        {!editing && !draft && (
          <fieldset className="mb-4">
            <legend className="text-sm font-medium">Build the card</legend>
            <div className="mt-1 flex flex-wrap gap-2" role="radiogroup">
              {(
                [
                  ['menus', 'Pick from menus'],
                  ['saved', 'From a saved query'],
                  ['write', 'Write the query (advanced)'],
                ] as const
              ).map(([value, label]) => (
                <Button
                  key={value}
                  role="radio"
                  aria-checked={way === value}
                  variant={way === value ? 'outline' : 'secondary'}
                  size="md"
                  onClick={() => setWay(value)}
                  disabled={saving}
                >
                  {label}
                </Button>
              ))}
            </div>
          </fieldset>
        )}

        {!editing && way === 'menus' && (
          <MenuBuilder
            qros={qros}
            disabled={saving}
            onBuilt={(built) => {
              setKind(built.kind);
              setSparql(built.sparql);
              if (!titleTouched) setTitle(built.title);
            }}
          />
        )}

        {!editing && way === 'saved' && (
          <SavedQueryPicker disabled={saving} onPick={(content) => setSparql(content)} />
        )}

        <label className="mt-4 block">
          <span className="text-sm font-medium">Title</span>
          <input
            ref={titleRef}
            type="text"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setTitleTouched(true);
            }}
            disabled={saving}
            className={fieldClass('normal', 'mt-1 w-full')}
            placeholder="e.g. Active products"
            required
          />
        </label>

        <label className="mt-4 block">
          <span className="text-sm font-medium">Subtitle</span>
          <input
            type="text"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            disabled={saving}
            className={fieldClass('normal', 'mt-1 w-full')}
            placeholder="e.g. across all releases"
          />
        </label>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {way === 'menus' && !editing ? (
            <div className="text-sm">
              <span className="font-medium">Shows</span>
              <p className="mt-1 text-tres-text-secondary">{KIND_SHOWS[kind]}</p>
            </div>
          ) : (
            <label className="block">
              <span className="text-sm font-medium">Shows</span>
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as CardKind)}
                disabled={saving || !!editing}
                className={fieldClass('normal', 'mt-1 w-full')}
              >
                <option value="count">A single number</option>
                <option value="distribution">A bar list of the top ten</option>
                <option value="table">A table</option>
              </select>
              {editing && (
                <span className="mt-1 block text-xs text-tres-text-secondary">
                  A card keeps what it shows; make a new card to change it.
                </span>
              )}
            </label>
          )}

          <label className="block">
            <span className="text-sm font-medium">Dashboard</span>
            <input
              type="text"
              value={dashboard}
              onChange={(e) => setDashboard(e.target.value)}
              disabled={saving}
              className={fieldClass('normal', 'mt-1 w-full')}
            />
            <span className="mt-1 block text-xs text-tres-text-secondary">
              The tab the card appears on. A new name makes a new tab; System holds the built-in cards.
            </span>
          </label>

          <label className="block">
            <span className="text-sm font-medium">Order</span>
            <input
              type="number"
              value={ordinal}
              onChange={(e) => setOrdinal(parseInt(e.target.value, 10) || 0)}
              disabled={saving}
              className={fieldClass('normal', 'mt-1 w-full')}
            />
            <span className="mt-1 block text-xs text-tres-text-secondary">
              Lower numbers come first. Built-in cards come before yours.
            </span>
          </label>
        </div>

        {(way !== 'menus' || editing) && (
          <label className="mt-4 block">
            <span className="text-sm font-medium">Query</span>
            <textarea
              value={sparql}
              onChange={(e) => setSparql(e.target.value)}
              disabled={saving}
              rows={6}
              className={fieldClass('normal', 'mt-1 w-full font-mono text-xs')}
              placeholder={QUERY_PLACEHOLDERS[qros ? 'qros' : 'rdf'][kind]}
              spellCheck={false}
            />
            <span className="mt-1 block text-xs text-tres-text-secondary">
              {kind === 'count'
                ? 'A single number: the query returns one row with one whole number.'
                : kind === 'distribution'
                  ? 'A bar list: each row names a thing and gives a number; a label is optional.'
                  : `A table: any ${qros ? 'FIND' : 'SELECT'}; columns follow the query, up to 50 rows.`}
            </span>
          </label>
        )}

        {saveState.status === 'error' && (
          <Message kind="warning" className="mt-4">
            {saveState.message}
          </Message>
        )}
      </form>
    </Modal>
  );
}

function MenuBuilder({
  qros,
  disabled,
  onBuilt,
}: {
  qros: boolean;
  disabled: boolean;
  onBuilt: (built: { kind: CardKind; sparql: string; title: string }) => void;
}) {
  const auth = useAuth();
  const schemaQ = useOntologySchema(ALL_GRAPHS, auth.user?.id_token);
  const [classUri, setClassUri] = useState('');
  const [measure, setMeasure] = useState('count');

  const classes = useMemo(
    () => [...(schemaQ.data?.classes ?? [])].sort((a, b) => labelOf(a).localeCompare(labelOf(b))),
    [schemaQ.data],
  );
  const cls = classes.find((c) => c.uri === classUri);
  const offered = useMemo(
    () => (schemaQ.data && classUri ? propertiesOfClass(classUri, schemaQ.data) : null),
    [schemaQ.data, classUri],
  );

  useEffect(() => {
    if (!cls) return;
    const name = labelOf(cls);
    if (measure === 'count') {
      onBuilt({ kind: 'count', sparql: countQuery(cls.uri, qros), title: `Number of ${name}` });
      return;
    }
    const prop = offered?.properties.find((p) => p.uri === measure);
    if (!prop) return;
    const things = prop.kind === 'object';
    onBuilt({
      kind: things ? 'distribution' : 'table',
      sparql: breakdownQuery(cls.uri, prop.uri, things, qros),
      title: `${name} by ${labelOf(prop)}`,
    });
    // onBuilt is the parent's setter bundle; the card follows the menus only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cls, measure, offered, qros]);

  if (schemaQ.isError) {
    return (
      <Message kind="warning">
        The classes could not be read, so the menus are empty: {(schemaQ.error as Error).message}. Use a saved
        query or write the query instead.
      </Message>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <label className="block">
        <span className="text-sm font-medium">Class</span>
        <select
          value={classUri}
          onChange={(e) => {
            setClassUri(e.target.value);
            setMeasure('count');
          }}
          disabled={disabled || !schemaQ.data}
          className={fieldClass('normal', 'mt-1 w-full')}
        >
          <option value="">{schemaQ.data ? 'Choose a class…' : 'Reading the classes…'}</option>
          {classes.map((c) => (
            <option key={c.uri} value={c.uri}>
              {labelOf(c)}
            </option>
          ))}
        </select>
        {schemaQ.data && classes.length === 0 && (
          <span className="mt-1 block text-xs text-tres-text-secondary">
            This database declares no classes yet.
          </span>
        )}
      </label>
      <label className="block">
        <span className="text-sm font-medium">The card counts</span>
        <select
          value={measure}
          onChange={(e) => setMeasure(e.target.value)}
          disabled={disabled || !cls}
          className={fieldClass('normal', 'mt-1 w-full')}
        >
          <option value="count">How many there are</option>
          {offered?.properties.map((p) => (
            <option key={p.uri} value={p.uri}>
              How many by {labelOf(p)}
            </option>
          ))}
        </select>
        {offered && !offered.declared && (
          <span className="mt-1 block text-xs text-tres-text-secondary">
            No property names this class as its domain, so every property is listed.
          </span>
        )}
      </label>
    </div>
  );
}

function SavedQueryPicker({ disabled, onPick }: { disabled: boolean; onPick: (content: string) => void }) {
  const snippets = useSnippets();
  const [picked, setPicked] = useState('');
  const list = snippets.data?.snippets ?? [];
  if (snippets.isError) {
    return <Message kind="warning">The saved queries could not be read: {(snippets.error as Error).message}</Message>;
  }
  return (
    <label className="block">
      <span className="text-sm font-medium">Saved query</span>
      <select
        value={picked}
        onChange={(e) => {
          setPicked(e.target.value);
          const s = list.find((x) => x.id === e.target.value);
          if (s) onPick(s.content);
        }}
        disabled={disabled || !snippets.data}
        className={fieldClass('normal', 'mt-1 w-full')}
      >
        <option value="">{snippets.data ? 'Choose a saved query…' : 'Reading the saved queries…'}</option>
        {list.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </select>
      {snippets.data && list.length === 0 && (
        <span className="mt-1 block text-xs text-tres-text-secondary">
          No saved queries yet. Save one from the Query page, or pick from menus.
        </span>
      )}
    </label>
  );
}
